import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface Milestone {
  id: number;
  title: string;
  description: string;
  completed: boolean;
  date?: string;
}

interface ProgressTrackerProps {
  milestones: Milestone[];
  currentLevel: string;
  totalPoints: number;
}

const ProgressTracker: React.FC<ProgressTrackerProps> = ({
  milestones,
  currentLevel,
  totalPoints,
}) => {
  const completedCount = milestones.filter((m) => m.completed).length;
  const progressPercentage = (completedCount / milestones.length) * 100;

  return (
    <div className="bg-card rounded-lg p-6 border border-border">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-headline font-bold text-foreground mb-1">
            Your Journey Progress
          </h3>
          <p className="text-sm text-muted-foreground">
            Current Level: <span className="font-semibold text-primary">{currentLevel}</span>
          </p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-accent">{totalPoints}</div>
          <div className="text-xs text-muted-foreground">Impact Points</div>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">
            {completedCount} of {milestones.length} milestones
          </span>
          <span className="text-sm font-medium text-primary">
            {Math.round(progressPercentage)}%
          </span>
        </div>
        <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-500"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>

      <div className="space-y-4">
        {milestones.map((milestone, index) => (
          <div key={milestone.id} className="flex items-start space-x-4">
            <div className="flex-shrink-0 mt-1">
              {milestone.completed ? (
                <div className="w-8 h-8 bg-success rounded-full flex items-center justify-center">
                  <Icon name="CheckIcon" size={20} className="text-white" />
                </div>
              ) : (
                <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-muted-foreground">
                    {index + 1}
                  </span>
                </div>
              )}
            </div>
            <div className="flex-1">
              <h4
                className={`text-sm font-semibold mb-1 ${
                  milestone.completed ? 'text-foreground' : 'text-muted-foreground'
                }`}
              >
                {milestone.title}
              </h4>
              <p className="text-xs text-muted-foreground mb-1">{milestone.description}</p>
              {milestone.date && (
                <p className="text-xs text-success font-medium">Completed: {milestone.date}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProgressTracker;